import React, { useEffect, useState } from 'react';

import { WindowLocation } from '@reach/router';
import { GlobalBodyStyles, GlobalBodyStylesProps, StyledMain } from './GlobalMain.styled';
import { MainLayout } from 'molecules/Header/Header/Header';
import { TypographyGlobalVariables } from 'styles/typography';
import { ColorPrimitiveGlobalVariables, ShadowGlobalVariables } from 'styles/colors';
import { ColorModeGlobalVariables, GradientGlobalVariables } from 'styles/colors';
import { SpacingGlobalVariables } from 'styles/spacing';
import { ExpandedMenuControllerProvider } from 'src/hooks/useExpandedMenuController';
import { TopInfoBarControllerProvider, TopInfoBarControllerProviderProps } from 'src/hooks/useTopInfoBarController';

import 'styles/fonts.scss';
import Footer from 'molecules/Footer/Footer';

import LanguageSwitcherPopup from 'molecules/LanguageSwitcherPopup/LanguageSwitcherPopup';
import Stack from 'atoms/Layout/Stack/Stack';
import HeaderAndInfoBarWrapper from '../HeaderAndInfoBarWrapper/HeaderAndInfoBarWrapper';
import getQueryParams from 'src/functions/getQueryParams';
import { getSessionStorage, setSessionStorage } from 'src/functions/localStorageHandler';
import { useI18nContext } from 'src/hooks/useI18nContext';
import { useBrowserLanguageDetection } from 'src/hooks/useBrowserLanguageDetection';
import { isUntranslatedPath } from 'src/utils/untranslated-pages';

interface GlobalMainTheme extends GlobalBodyStylesProps {
  hidePopup?: boolean;
}

interface GlobalMainProps<LocationState = WindowLocation['state']> {
  children: React.ReactNode;
  location?: WindowLocation<LocationState>;
  mainLayout?: MainLayout;
  mainTheme?: GlobalMainTheme;
  topbar?: boolean | (() => JSX.Element);
  footer?: boolean | (() => JSX.Element);
  infoBarProps?: Omit<TopInfoBarControllerProviderProps, 'children'> | false;
}

const GlobalMain = ({
  children,
  location,
  mainLayout = 'standard',
  mainTheme = {},
  topbar = true,
  footer = true,
  infoBarProps,
}: GlobalMainProps): JSX.Element => {
  const [inAppView, setInAppView] = useState(false);
  const { language } = useI18nContext();
  const { detectedLanguage, shouldShowPopup, dismissPopup, acceptLanguage } = useBrowserLanguageDetection(language);

  useEffect(() => {
    if (typeof window !== 'undefined') {
      const queryParams = getQueryParams();
      const inAppView = queryParams.hide_chrome === '1';

      if (inAppView) {
        setSessionStorage('hide_chrome', '1');
        setInAppView(true);
      } else {
        const inAppViewCache = getSessionStorage('hide_chrome') === '1';
        setInAppView(inAppViewCache);
      }
    }
  }, []);

  // Auto-detect if language switcher popup should be hidden for untranslated pages
  const isPageUntranslated = location?.pathname ? isUntranslatedPath(location.pathname) : false;

  const renderLanguageSwitcherPopup = () => {
    if (isPageUntranslated || !shouldShowPopup || !detectedLanguage) return null;

    return (
      <LanguageSwitcherPopup
        visible={shouldShowPopup}
        detectedLanguage={detectedLanguage}
        onAccept={acceptLanguage}
        onDismiss={dismissPopup}
      />
    );
  };

  const renderFooter = () => {
    if (inAppView || !footer) return null;
    if (typeof footer === 'function') return footer();

    return <Footer mainLayout={mainLayout} />;
  };

  const renderHeader = () => {
    if (inAppView || !topbar) return null;
    if (typeof topbar === 'function') return topbar();

    return (
      <ExpandedMenuControllerProvider>
        <HeaderAndInfoBarWrapper
          headerProps={{
            locationProps: location,
            mainLayout: mainLayout,
          }}
        />
      </ExpandedMenuControllerProvider>
    );
  };

  const renderContent = () => (
    <StyledMain id='global-main'>
      <SpacingGlobalVariables />
      <ColorPrimitiveGlobalVariables />
      <GradientGlobalVariables />
      <ColorModeGlobalVariables />
      <ShadowGlobalVariables />
      <TypographyGlobalVariables />
      <GlobalBodyStyles backgroundColor={mainTheme.backgroundColor} />
      <Stack direction='column'>
        {renderLanguageSwitcherPopup()}
        {renderHeader()}
        {children}
        {renderFooter()}
      </Stack>
    </StyledMain>
  );

  // Wrap content with appropriate InfoBar based on props
  const wrapWithInfoBar = (content: JSX.Element): JSX.Element => {
    // If infoBarProps is false (default) or undefined, don't show any InfoBar
    if (!infoBarProps) {
      return content;
    }

    // If specific infoBarProps provided, use them
    return <TopInfoBarControllerProvider {...infoBarProps}>{content}</TopInfoBarControllerProvider>;
  };

  return wrapWithInfoBar(renderContent());
};

export default GlobalMain;
